/**
 * VacationsTab — Saldos de vacaciones, solicitudes, permisos.
 * Tres secciones: Resumen por empleado, Solicitudes de vacaciones, Permisos/Licencias.
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Calendar, Plus, CheckCircle, XCircle, Sun, Briefcase } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useVacationRequests, useCreateVacationRequest, useApproveVacation, useRejectVacation, useCancelVacation, useEmployees, useBusinessDays, useLeaveRequests } from '../hooks/usePayroll'
import { DialogoDeMotivo } from './DialogoDeMotivo'
import { MotivoDeLaDecision } from '@/components/MotivoDeLaDecision'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { extractErrorMessage } from '@/lib/errorUtils'
import DatePicker from '@/components/DatePicker'
import { inputCls, labelCls } from '@/lib/formStyles'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { VACATION_REQUEST_STATUS, LEAVE_REQUEST_STATUS } from '../../../components/ds/statusCatalogs'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { formatoDias, useBeneficiosDeLaFicha } from '../hooks/useBeneficios'


// Hooks
function useVacationSummary() {
  return useQuery({ queryKey: ['vacation-summary'], queryFn: async () => { const r = await apiClient.get('/payroll/vacations/summary/'); return r.data } })
}
/** Un permiso que es un beneficio de días mueve su saldo (#1765): se refresca junto con la lista. */
function invalidarPermisos(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['leave-requests'] })
  qc.invalidateQueries({ queryKey: ['employee-beneficios'] })
}
function useCreateLeave() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/leaves/', d); return r.data }, onSuccess: () => invalidarPermisos(qc) })
}
function useApproveLeave(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post(`/payroll/leaves/${id}/approve/`); return r.data }, onSuccess: () => invalidarPermisos(qc) })
}
function useRejectLeave(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (reason: string) => { const r = await apiClient.post(`/payroll/leaves/${id}/reject/`, { reason }); return r.data }, onSuccess: () => invalidarPermisos(qc) })
}
function useCancelLeave(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (reason: string) => { const r = await apiClient.post(`/payroll/leaves/${id}/cancel/`, { reason }); return r.data }, onSuccess: () => invalidarPermisos(qc) })
}

/**
 * Personas presentes en los registros, para poblar el filtro.
 *
 * Se arma de los propios datos y NO del catálogo de empleados por dos razones:
 * incluye a los desvinculados que tienen historial (el catálogo devuelve solo
 * activos salvo que se le pida lo contrario), y no exige el permiso
 * `payroll.employees` — quien administra vacaciones puede no tenerlo, y el
 * filtro quedaría vacío sin ninguna señal.
 */
export function personasDeLosRegistros(items: any[]): { id: string; nombre: string }[] {
  const m = new Map<string, string>()
  for (const it of items) {
    if (it.employee) m.set(it.employee, it.employee_name || 'Sin nombre')
  }
  return [...m.entries()]
    .map(([id, nombre]) => ({ id, nombre }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
}

/**
 * Filtros del listado. Se resuelven en el cliente a propósito: la vista del
 * backend devuelve el conjunto completo sin paginar (su `list()` sobrescrito
 * ignora la `pagination_class`), así que el navegador ya tiene todas las filas
 * y filtrar en el servidor solo agregaría un viaje por cada cambio. Cuando esa
 * vista pagine de verdad, el filtro se mueve al servidor: los endpoints ya
 * aceptan `employee_id`, `status` y `leave_type`.
 */
function FiltrosDeRegistros({
  personas, persona, setPersona, estado, setEstado, estados, orden, setOrden,
  extra, extraActivo, limpiarExtra,
}: {
  personas: { id: string; nombre: string }[]
  persona: string; setPersona: (v: string) => void
  estado: string; setEstado: (v: string) => void
  estados: { value: string; label: string }[]
  orden: 'desc' | 'asc'; setOrden: (v: 'desc' | 'asc') => void
  // Filtro propio de la sección (el tipo, en permisos). Va con su estado y su
  // limpieza para que «Limpiar filtros» no deje uno puesto sin que se note.
  extra?: React.ReactNode
  extraActivo?: boolean
  limpiarExtra?: () => void
}) {
  const hayFiltro = !!persona || !!estado || !!extraActivo
  const selCls = `${inputCls} py-1.5 text-sm`
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={persona} onChange={e => setPersona(e.target.value)}
        className={`${selCls} max-w-[16rem]`} aria-label="Filtrar por trabajador">
        <option value="">Todos los trabajadores</option>
        {personas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
      </select>
      <select value={estado} onChange={e => setEstado(e.target.value)}
        className={`${selCls} max-w-[12rem]`} aria-label="Filtrar por estado">
        <option value="">Todos los estados</option>
        {estados.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
      </select>
      {extra}
      <select value={orden} onChange={e => setOrden(e.target.value as 'desc' | 'asc')}
        className={`${selCls} max-w-[13rem]`} aria-label="Ordenar por fecha">
        <option value="desc">Más recientes primero</option>
        <option value="asc">Más antiguas primero</option>
      </select>
      {hayFiltro && (
        <button onClick={() => { setPersona(''); setEstado(''); limpiarExtra?.() }}
          className="px-2.5 py-1.5 text-sm text-content-muted hover:text-content underline">
          Limpiar filtros
        </button>
      )}
    </div>
  )
}

/** Aplica filtro por persona/estado y ordena por fecha de inicio. */
export function filtrarYOrdenar<T extends { employee?: string; status?: string; start_date?: string }>(
  items: T[], persona: string, estado: string, orden: 'desc' | 'asc',
): T[] {
  const out = items.filter(i =>
    (!persona || i.employee === persona) && (!estado || i.status === estado))
  return [...out].sort((a, b) => {
    const c = (a.start_date || '').localeCompare(b.start_date || '')
    return orden === 'asc' ? c : -c
  })
}

type Seccion = 'summary' | 'vacations' | 'leaves'
const SECCIONES: Seccion[] = ['summary', 'vacations', 'leaves']

/** Fila que trae un aviso (`?highlight=<id>`): se marca para encontrarla a la primera. */
const FILA_RESALTADA = 'bg-primary-50 ring-1 ring-inset ring-primary/40'

export default function VacationsTab() {
  // Los avisos de solicitud llegan con `?tab=vacations|leaves&highlight=<id>` (#1473):
  // sin esto la pantalla abría en «Saldos» y había que buscar la solicitud a mano.
  const [params] = useSearchParams()
  const inicial = params.get('tab') as Seccion | null
  const [tab, setTab] = useState<Seccion>(inicial && SECCIONES.includes(inicial) ? inicial : 'summary')
  const resaltar = params.get('highlight') || undefined
  const { can } = usePermission()
  const toast = useToast()

  return (
    <>
      {/* Tab selector */}
      <div className="flex gap-1 mb-5 bg-glass-3 rounded-lg p-1 w-fit">
        {[
          { key: 'summary', label: 'Saldos', icon: Sun },
          { key: 'vacations', label: 'Solicitudes Vacaciones', icon: Calendar },
          { key: 'leaves', label: 'Permisos / Licencias', icon: Briefcase },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              tab === t.key ? 'bg-surface text-content shadow-sm' : 'text-content-muted hover:text-content'
            }`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'summary' && <SummarySection />}
      {tab === 'vacations' && <VacationsSection toast={toast} can={can} resaltar={resaltar} />}
      {tab === 'leaves' && <LeavesSection toast={toast} can={can} resaltar={resaltar} />}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Summary Section ────────────────────────────────────────────────────────

function SummarySection() {
  const { data, isLoading } = useVacationSummary()
  const summary = data?.summary || []

  return (
    <DataTable<any>
      columns={[
        { key: 'employee_name', header: 'Empleado', sortable: true,
          render: s => <span className="font-medium text-content">{s.employee_name}</span> },
        { key: 'employee_rut', header: 'RUT', sortable: true,
          render: s => <span className="text-xs text-content-subtle font-mono">{s.employee_rut}</span> },
        { key: 'years_of_service', header: 'Años Servicio', numeric: true, sortable: true,
          sortValue: s => Number(s.years_of_service) },
        { key: 'days_entitled', header: 'Días/Año', numeric: true, sortable: true,
          sortValue: s => Number(s.days_entitled),
          render: s => (
            <>
              <span className="font-medium">{s.days_entitled}</span>
              {parseInt(s.days_entitled) > 15 && (
                <span className="ml-1 text-[10px] text-purple-500"
                  title={`15 base + ${parseInt(s.days_entitled) - 15} por antigüedad (Art. 68 CT)`}>
                  (+{parseInt(s.days_entitled) - 15})
                </span>
              )}
            </>
          ) },
        // Las dos bolsas van separadas porque son derechos distintos: el
        // progresivo del Art. 68 SÍ se puede compensar en dinero y el legal no.
        { key: 'days_pending_legal', header: 'Legales', numeric: true, sortable: true,
          sortValue: s => Number(s.days_pending_legal ?? 0),
          render: s => <span className="text-content-muted">{s.days_pending_legal ?? '—'}</span> },
        { key: 'days_pending_progressive', header: 'Progresivas', numeric: true, sortable: true,
          sortValue: s => Number(s.days_pending_progressive ?? 0),
          render: s => (parseFloat(s.days_pending_progressive ?? '0') > 0 ? (
            <span className="text-content-muted">{s.days_pending_progressive}</span>
          ) : (
            <span className="text-content-subtle" title="Sin bolsa de feriado progresivo cargada">—</span>
          )) },
        { key: 'days_pending', header: 'Total Pendientes', numeric: true, sortable: true,
          sortValue: s => Number(s.days_pending),
          render: s => (
            <span className={`font-bold ${parseFloat(s.days_pending) > 0 ? 'text-success' : 'text-content-subtle'}`}>
              {s.days_pending}
            </span>
          ) },
      ]}
      rows={summary}
      rowKey={s => s.employee_id}
      minWidth={880}
      loading={isLoading}
      empty={<EmptyState title="Sin empleados." size="sm" />}
      caption="Saldo de vacaciones por trabajador, separado en sus dos bolsas"
    />
  )
}

// ─── Vacations Section ──────────────────────────────────────────────────────

function VacationsSection({ toast, can, resaltar }: { toast: any; can: any; resaltar?: string }) {
  const locale = useTenantLocale()
  const { data } = useVacationRequests()
  const [creating, setCreating] = useState(false)
  const [persona, setPersona] = useState('')
  const [estado, setEstado] = useState('')
  const [orden, setOrden] = useState<'desc' | 'asc'>('desc')
  // Memorizado porque `data?.requests || []` devuelve un array nuevo en cada
  // render: sin esto los useMemo de abajo se recalculan siempre y no memorizan.
  const todas = useMemo(() => data?.requests || [], [data])

  const personas = useMemo(() => personasDeLosRegistros(todas), [todas])
  const requests = useMemo(
    () => filtrarYOrdenar(todas, persona, estado, orden), [todas, persona, estado, orden])
  // Con una persona seleccionada esto es su historial, así que el total de días
  // es lo que se contrasta contra el saldo o contra el sistema anterior.
  const dias = useMemo(
    () => requests.reduce((s: number, r: any) => s + parseFloat(r.days_requested || 0), 0), [requests])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <span className="text-sm text-content-muted">
          {requests.length === todas.length
            ? `${todas.length} solicitudes`
            : `${requests.length} de ${todas.length} solicitudes`}
          {requests.length > 0 && (
            <> · <span className="font-medium text-content">{dias.toLocaleString('es-CL')} días</span></>
          )}
        </span>
        {can('payroll.vacations', 'create') && (
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nueva solicitud
          </button>
        )}
      </div>

      {creating && <VacationForm onClose={() => setCreating(false)} toast={toast} />}

      <FiltrosDeRegistros
        personas={personas} persona={persona} setPersona={setPersona}
        estado={estado} setEstado={setEstado} orden={orden} setOrden={setOrden}
        estados={[
          { value: 'pending', label: 'Pendiente' },
          { value: 'approved', label: 'Aprobado' },
          { value: 'taken', label: 'Gozadas' },
          { value: 'rejected', label: 'Rechazado' },
          { value: 'cancelled', label: 'Anulada' },
        ]}
      />

      <DataTable<any>
        columns={[
          { key: 'employee_name', header: 'Empleado', sortable: true,
            render: r => (
              <>
                <span className="font-medium text-content">{r.employee_name}</span>
                {/* La nota de quien pidió: antes no se veía en ninguna pantalla de decisión. */}
                {r.notes && (
                  <div className="text-[11px] text-content-subtle mt-0.5 max-w-[16rem] whitespace-normal">
                    Nota: {r.notes}
                  </div>
                )}
              </>
            ) },
          { key: 'start_date', header: 'Desde', sortable: true,
            render: r => <span className="text-content-muted">{formatDate(r.start_date, locale)}</span> },
          { key: 'end_date', header: 'Hasta', sortable: true,
            render: r => <span className="text-content-muted">{formatDate(r.end_date, locale)}</span> },
          { key: 'days_requested', header: 'Días', numeric: true, sortable: true,
            sortValue: r => Number(r.days_requested),
            render: r => <span className="font-medium">{r.days_requested}</span> },
          { key: 'record_vacation_type_display', header: 'Bolsa', sortable: true,
            render: r => (
              <span className="text-content-muted">{r.record_vacation_type_display || '—'}</span>
            ) },
          { key: 'status', header: 'Estado', sortable: true,
            render: r => (
              <>
                <StatusBadge status={r.status} catalog={VACATION_REQUEST_STATUS} />
                {r.status === 'pending_second' && (
                  <div className="text-[11px] text-content-subtle mt-1">
                    1.ª: {r.first_approved_by_name || '—'}
                    {r.segundo_aprobador_nombre && <> · falta {r.segundo_aprobador_nombre}</>}
                  </div>
                )}
                {r.status === 'approved' && r.approved_by_name && (
                  <div className="text-[11px] text-content-subtle mt-1">
                    por {r.approved_by_name}{r.approved_at ? ` · ${formatDate(r.approved_at, locale)}` : ''}
                  </div>
                )}
                <MotivoDeLaDecision status={r.status} motivo={r.decision_reason} />
                <EstadoDelComprobante comprobante={r.comprobante} />
              </>
            ) },
          { key: 'acciones', header: 'Acc.', width: 80,
            render: r => (
              <>
                {r.status === 'pending' && can('payroll.vacations', 'approve') && (
                  <VacationActions solicitud={r} toast={toast} />
                )}
                {(r.status === 'approved' || r.status === 'taken') && can('payroll.vacations', 'approve') && (
                  <CancelVacationAction solicitud={r} toast={toast} />
                )}
              </>
            ) },
        ]}
        rows={requests}
        rowClassName={r => (r.id === resaltar ? FILA_RESALTADA : undefined)}
        rowKey={r => r.id}
        minWidth={920}
        empty={
          <EmptyState
            title={todas.length === 0
              ? 'Sin solicitudes de vacaciones.'
              : 'Ninguna solicitud coincide con los filtros aplicados.'}
            size="sm"
          />
        }
        caption="Solicitudes de vacaciones y su estado"
      />
    </div>
  )
}

/**
 * El comprobante que se generó al aprobar (#1489) y si el trabajador ya lo firmó.
 * Sin pedido de firma se dice por qué (`sin_firma`), que es lo que RR. HH. tiene
 * que corregir antes de pedirla desde los documentos de la ficha.
 */
const MOTIVO_SIN_FIRMA: Record<string, string> = {
  sin_correo: 'falta el correo del trabajador',
  correo_en_otra_ficha: 'el correo ya es el acceso al portal de otra ficha',
}

function EstadoDelComprobante({ comprobante }: {
  comprobante?: { firma: string | null; sin_firma?: string | null } | null
}) {
  if (!comprobante) return null
  const motivo = comprobante.sin_firma ? MOTIVO_SIN_FIRMA[comprobante.sin_firma] : undefined
  const texto = comprobante.firma === 'signed' ? 'Comprobante firmado'
    : comprobante.firma === 'pending' ? 'Comprobante por firmar'
    : motivo ? `Comprobante sin firma: ${motivo}` : 'Comprobante sin firma'
  const tono = comprobante.firma === 'signed' ? 'text-success-content' : 'text-content-subtle'
  return <div className={`text-[11px] mt-1 ${tono}`}>{texto}</div>
}

/** «Juan Pérez · 10-11 al 14-11», para el subtítulo de los diálogos. */
function glosaDeSolicitud(s: any, locale: string) {
  const rango = s.start_date === s.end_date
    ? formatDate(s.start_date, locale)
    : `${formatDate(s.start_date, locale)} al ${formatDate(s.end_date, locale)}`
  return `${s.employee_name} · ${rango}`
}

function VacationActions({ solicitud, toast }: { solicitud: any; toast: any }) {
  const locale = useTenantLocale()
  const { mutate: approve } = useApproveVacation(solicitud.id)
  const reject = useRejectVacation(solicitud.id)
  const { confirm, modalProps } = useConfirmModal()
  const [rechazando, setRechazando] = useState(false)

  return (
    <div className="flex justify-center gap-1">
      <button onClick={() => confirm({ message: '¿Aprobar vacaciones?', variant: 'success',
        onConfirm: () => approve(undefined, { onSuccess: () => toast.success('Aprobado'), onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')) }) })}
        title="Aprobar" className="p-1 rounded hover:bg-success-surface text-success-solid"><CheckCircle size={15} /></button>
      {/* Antes este botón confirmaba y no hacía nada (#1535). */}
      <button onClick={() => setRechazando(true)}
        title="Rechazar" className="p-1 rounded hover:bg-danger-surface text-danger-solid"><XCircle size={15} /></button>
      <ConfirmModal {...modalProps} />
      {rechazando && (
        <DialogoDeMotivo
          titulo="Rechazar vacaciones" subtitulo={glosaDeSolicitud(solicitud, locale)}
          ayuda="El motivo le llega por correo a quien pidió las vacaciones."
          confirmar="Rechazar" pendiente={reject.isPending}
          onCerrar={() => setRechazando(false)}
          onConfirmar={(motivo, alFallar) => reject.mutate(motivo, {
            onSuccess: () => { toast.success('Vacaciones rechazadas'); setRechazando(false) },
            onError: alFallar,
          })}
        />
      )}
    </div>
  )
}

function CancelVacationAction({ solicitud, toast }: { solicitud: any; toast: any }) {
  const locale = useTenantLocale()
  const cancel = useCancelVacation(solicitud.id)
  const [anulando, setAnulando] = useState(false)

  return (
    <div className="flex justify-center">
      <button onClick={() => setAnulando(true)}
        title="Anular y devolver saldo"
        className="px-2 py-1 text-xs rounded border border-divider hover:bg-danger-surface text-danger-solid">Anular</button>
      {anulando && (
        <DialogoDeMotivo
          titulo="Anular vacaciones" subtitulo={glosaDeSolicitud(solicitud, locale)}
          ayuda="Los días vuelven al saldo del trabajador, que recibe el motivo por correo."
          confirmar="Anular" pendiente={cancel.isPending}
          onCerrar={() => setAnulando(false)}
          onConfirmar={(motivo, alFallar) => cancel.mutate(motivo, {
            onSuccess: () => { toast.success('Vacaciones anuladas, saldo devuelto'); setAnulando(false) },
            onError: alFallar,
          })}
        />
      )}
    </div>
  )
}

/**
 * Campo "Días hábiles" con autocálculo: dadas las fechas desde/hasta, consulta el
 * backend (excluye domingos, sábados y feriados) y autocompleta el valor, mostrando
 * el desglose. El usuario puede corregir el número a mano si lo necesita.
 */
function BusinessDaysField({ startDate, endDate, value, onChange, label = 'Días hábiles', className = 'col-span-2' }:
  { startDate: string; endDate: string; value: string; onChange: (v: string) => void; label?: string; className?: string }) {
  const bd = useBusinessDays(startDate, endDate)
  const d = bd.data

  // Autocompleta cuando llega un cálculo nuevo (cambió el rango de fechas).
  useEffect(() => {
    if (d && !d.error) onChange(String(d.business_days))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.business_days, d?.error])

  return (
    <div className={className}>
      <label className={labelCls}>{label}</label>
      <input aria-label={label} type="number" min="0" step="0.5" value={value}
        onChange={e => onChange(e.target.value)} className={inputCls}
        placeholder={startDate && endDate ? 'Calculando…' : 'Elegir las fechas'} />
      {d && !d.error && (
        <p className="text-[11px] text-content-subtle mt-1 leading-snug">
          <span className="font-medium text-content-muted">{d.business_days} hábiles</span>
          {' · '}{d.calendar_days} corridos · {d.weekend_days} fines de semana
          {d.holiday_days > 0 && ` · ${d.holiday_days} feriado(s)`}
          {d.holidays.length > 0 && (
            <span className="block text-content-subtle">
              Feriados en el rango: {d.holidays.map(h => `${h.date.slice(8, 10)}-${h.date.slice(5, 7)} ${h.name}`).join(' · ')}
            </span>
          )}
        </p>
      )}
      {d?.error && <p className="text-[11px] text-danger-solid mt-1">{d.error}</p>}
      {bd.isFetching && !d && <p className="text-[11px] text-content-subtle mt-1">Calculando…</p>}
    </div>
  )
}

function VacationForm({ onClose, toast }: { onClose: () => void; toast: any }) {
  const { data: empData } = useEmployees({ status: 'active', page_size: 9999 })
  const { mutate: create, isPending } = useCreateVacationRequest()
  const [employee, setEmployee] = useState('')
  const [vacationType, setVacationType] = useState<'legal' | 'progressive'>('legal')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [days, setDays] = useState('')
  const [notes, setNotes] = useState('')

  const handleSubmit = () => {
    if (!employee || !startDate || !endDate || !days) return
    // El backend resuelve el `vacation_record` concreto a partir de la bolsa
    // elegida (el período más antiguo, que es el que primero prescribe).
    create({
      employee, vacation_type: vacationType,
      start_date: startDate, end_date: endDate, days_requested: days, notes,
    } as any, {
      onSuccess: () => { toast.success('Solicitud creada'); onClose() },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  return (
    <div className="p-4 bg-glass-3 border border-divider rounded-xl space-y-3">
      <p className="text-xs font-bold text-content-muted uppercase tracking-wider">Nueva Solicitud de Vacaciones</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SelectField
          dense
          className="col-span-2"
          label="Empleado"
          value={employee}
          onChange={e => setEmployee(e.target.value)}
        >
          <option value="">Seleccionar...</option>
          {(empData?.employees || []).map((e: any) => (
          <option key={e.id} value={e.id}>{e.full_name || `${e.first_name} ${e.last_name}`}</option>
          ))}
        </SelectField>
        <SelectField
          dense
          className="col-span-2"
          label="Bolsa de días"
          value={vacationType}
          onChange={e => setVacationType(e.target.value as 'legal' | 'progressive')}
          hint="El feriado progresivo se puede compensar en dinero antes del feriado anual; el legal no."
        >
          <option value="legal">Feriado legal (Art. 67)</option>
          <option value="progressive">Feriado progresivo (Art. 68)</option>
        </SelectField>
        <div>
          <label htmlFor="vacationstab-desde-2" className={labelCls}>Desde</label>
          <DatePicker id="vacationstab-desde-2" value={startDate} onChange={setStartDate} />
        </div>
        <div>
          <label htmlFor="vacationstab-hasta-2" className={labelCls}>Hasta</label>
          <DatePicker id="vacationstab-hasta-2" value={endDate} onChange={setEndDate} />
        </div>
        <BusinessDaysField startDate={startDate} endDate={endDate} value={days} onChange={setDays} />
        <TextField
          dense
          className="col-span-2"
          label="Notas"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Opcional"
          inputProps={{ type: 'text' }}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-surface">Cancelar</button>
        <button onClick={handleSubmit} disabled={isPending || !employee || !startDate}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40">
          Crear solicitud
        </button>
      </div>
    </div>
  )
}

// ─── Leaves Section ─────────────────────────────────────────────────────────

function LeavesSection({ toast, can, resaltar }: { toast: any; can: any; resaltar?: string }) {
  const locale = useTenantLocale()
  const { data } = useLeaveRequests()
  const [creating, setCreating] = useState(false)
  const [persona, setPersona] = useState('')
  const [estado, setEstado] = useState('')
  const [tipo, setTipo] = useState('')
  const [orden, setOrden] = useState<'desc' | 'asc'>('desc')
  // Ver la nota del mismo patrón en VacationsSection.
  const todos = useMemo(() => data?.leaves || [], [data])

  const personas = useMemo(() => personasDeLosRegistros(todos), [todos])
  const leaves = useMemo(
    () => filtrarYOrdenar(todos, persona, estado, orden)
      .filter((l: any) => !tipo || l.leave_type === tipo),
    [todos, persona, estado, tipo, orden])
  const dias = useMemo(
    () => leaves.reduce((s: number, l: any) => s + parseFloat(l.days || 0), 0), [leaves])
  // Los tipos salen de los datos y no de una lista fija: así el filtro no puede
  // quedar desalineado con los `leave_type` que el backend agregue después.
  const tipos = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of todos) if (l.leave_type) m.set(l.leave_type, l.leave_type_display || l.leave_type)
    return [...m.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'))
  }, [todos])

  const LEAVE_BADGE: Record<string, string> = {
    paid_leave: 'bg-success-surface-strong text-success-content',
    unpaid_leave: 'bg-warning-surface-strong text-warning-content',
    medical: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    legal: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    paternity: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
    bereavement: 'bg-surface-muted text-content',
    marriage: 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300',
    other: 'bg-surface-muted text-content-muted',
    benefit: 'bg-info-surface text-info-content',
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <span className="text-sm text-content-muted">
          {leaves.length === todos.length
            ? `${todos.length} permisos/licencias`
            : `${leaves.length} de ${todos.length} permisos/licencias`}
          {leaves.length > 0 && (
            <> · <span className="font-medium text-content">{dias.toLocaleString('es-CL')} días</span></>
          )}
        </span>
        {can('payroll.vacations', 'create') && (
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nuevo permiso
          </button>
        )}
      </div>

      {creating && <LeaveForm onClose={() => setCreating(false)} toast={toast} />}

      <FiltrosDeRegistros
        personas={personas} persona={persona} setPersona={setPersona}
        estado={estado} setEstado={setEstado} orden={orden} setOrden={setOrden}
        estados={[
          { value: 'pending', label: 'Pendiente' },
          { value: 'pending_second', label: 'Pendiente 2.ª aprobación' },
          { value: 'approved', label: 'Aprobado' },
          { value: 'rejected', label: 'Rechazado' },
          { value: 'cancelled', label: 'Anulado' },
        ]}
        extraActivo={!!tipo}
        limpiarExtra={() => setTipo('')}
        extra={
          <select value={tipo} onChange={e => setTipo(e.target.value)}
            className={`${inputCls} py-1.5 text-sm max-w-[14rem]`} aria-label="Filtrar por tipo">
            <option value="">Todos los tipos</option>
            {tipos.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        }
      />

      <DataTable<any>
        columns={[
          { key: 'employee_name', header: 'Empleado', sortable: true,
            render: l => (
              <>
                <span className="font-medium text-content">{l.employee_name}</span>
                {l.reason && (
                  <div className="text-[11px] text-content-subtle mt-0.5 max-w-[16rem] whitespace-normal">
                    Nota: {l.reason}
                  </div>
                )}
              </>
            ) },
          { key: 'leave_type_display', header: 'Tipo', sortable: true,
            render: l => (
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${LEAVE_BADGE[l.leave_type] || LEAVE_BADGE.other}`}>
                {l.leave_type_display}
              </span>
            ) },
          { key: 'start_date', header: 'Desde', sortable: true,
            render: l => <span className="text-content-muted">{formatDate(l.start_date, locale)}</span> },
          { key: 'end_date', header: 'Hasta', sortable: true,
            render: l => <span className="text-content-muted">{formatDate(l.end_date, locale)}</span> },
          { key: 'days', header: 'Días', numeric: true, sortable: true,
            sortValue: l => Number(l.days),
            render: l => <span className="font-medium">{l.days}</span> },
          { key: 'affects_salary', header: 'Afecta Sueldo', sortable: true,
            sortValue: l => (l.affects_salary ? 1 : 0),
            render: l => (l.affects_salary
              ? <span className="text-danger-solid font-medium">Sí</span>
              : <span className="text-content-subtle">No</span>) },
          { key: 'status', header: 'Estado', sortable: true,
            render: l => (
              <>
                <StatusBadge status={l.status} catalog={LEAVE_REQUEST_STATUS} />
                {l.status === 'pending_second' && (
                  <div className="text-[11px] text-content-subtle mt-1">
                    1.ª: {l.first_approved_by_name || '—'}
                    {l.segundo_aprobador_nombre && <> · falta {l.segundo_aprobador_nombre}</>}
                  </div>
                )}
                {l.parte_del_dia && (
                  <div className="text-[11px] text-content-subtle mt-1">
                    Medio día ({l.parte_del_dia === 'manana' ? 'mañana' : 'tarde'})
                  </div>
                )}
                {l.status === 'approved' && l.approved_by_name && (
                  <div className="text-[11px] text-content-subtle mt-1">
                    por {l.approved_by_name}{l.approved_at ? ` · ${formatDate(l.approved_at, locale)}` : ''}
                  </div>
                )}
                <MotivoDeLaDecision status={l.status} motivo={l.decision_reason} />
              </>
            ) },
          { key: 'acciones', header: 'Acc.', width: 80,
            render: l => {
              if (!can('payroll.vacations', 'approve')) return null
              if (l.status === 'pending') return <LeaveActions permiso={l} toast={toast} />
              if (l.status === 'approved') return <CancelLeaveAction permiso={l} toast={toast} />
              return null
            } },
        ]}
        rows={leaves}
        rowClassName={r => (r.id === resaltar ? FILA_RESALTADA : undefined)}
        rowKey={l => l.id}
        minWidth={980}
        // Distinguir «no hay datos» de «el filtro no los pide»: si no, un filtro
        // puesto se lee como que el registro está vacío.
        empty={
          <EmptyState
            title={todos.length === 0
              ? 'Sin permisos registrados.'
              : 'Ningún permiso coincide con los filtros aplicados.'}
            size="sm"
          />
        }
        caption="Permisos y licencias del personal"
      />
    </div>
  )
}

function LeaveActions({ permiso, toast }: { permiso: any; toast: any }) {
  const locale = useTenantLocale()
  const { mutate: approve } = useApproveLeave(permiso.id)
  const reject = useRejectLeave(permiso.id)
  const { confirm, modalProps } = useConfirmModal()
  const [rechazando, setRechazando] = useState(false)

  return (
    <div className="flex justify-center gap-1">
      <button onClick={() => confirm({ message: '¿Aprobar permiso?', variant: 'success',
        onConfirm: () => approve(undefined, { onSuccess: () => toast.success('Aprobado'), onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')) }) })}
        title="Aprobar" className="p-1 rounded hover:bg-success-surface text-success-solid"><CheckCircle size={15} /></button>
      <button onClick={() => setRechazando(true)}
        title="Rechazar" className="p-1 rounded hover:bg-danger-surface text-danger-solid"><XCircle size={15} /></button>
      <ConfirmModal {...modalProps} />
      {rechazando && (
        <DialogoDeMotivo
          titulo="Rechazar permiso" subtitulo={glosaDeSolicitud(permiso, locale)}
          ayuda="El motivo le llega por correo a quien pidió el permiso."
          confirmar="Rechazar" pendiente={reject.isPending}
          onCerrar={() => setRechazando(false)}
          onConfirmar={(motivo, alFallar) => reject.mutate(motivo, {
            onSuccess: () => { toast.success('Permiso rechazado'); setRechazando(false) },
            onError: alFallar,
          })}
        />
      )}
    </div>
  )
}

/**
 * Anular un permiso aprobado. ⚠️ El backend se niega si el permiso descuenta
 * días de un mes ya liquidado: el error se muestra dentro del diálogo.
 */
function CancelLeaveAction({ permiso, toast }: { permiso: any; toast: any }) {
  const locale = useTenantLocale()
  const cancel = useCancelLeave(permiso.id)
  const [anulando, setAnulando] = useState(false)

  return (
    <div className="flex justify-center">
      <button onClick={() => setAnulando(true)} title="Anular permiso"
        className="px-2 py-1 text-xs rounded border border-divider hover:bg-danger-surface text-danger-solid">Anular</button>
      {anulando && (
        <DialogoDeMotivo
          titulo="Anular permiso" subtitulo={glosaDeSolicitud(permiso, locale)}
          ayuda="El permiso queda sin efecto y el trabajador recibe el motivo por correo."
          confirmar="Anular" pendiente={cancel.isPending}
          onCerrar={() => setAnulando(false)}
          onConfirmar={(motivo, alFallar) => cancel.mutate(motivo, {
            onSuccess: () => { toast.success('Permiso anulado'); setAnulando(false) },
            onError: alFallar,
          })}
        />
      )}
    </div>
  )
}

function LeaveForm({ onClose, toast }: { onClose: () => void; toast: any }) {
  const { data: empData } = useEmployees({ status: 'active', page_size: 9999 })
  const { mutate: create, isPending } = useCreateLeave()
  const [employee, setEmployee] = useState('')
  const [leaveType, setLeaveType] = useState('paid_leave')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [days, setDays] = useState('')
  const [reason, setReason] = useState('')
  // Beneficio de días (#1765): se elige entre los de la ficha, con su saldo. Los
  // días los calcula el servidor (hábiles del rango, o 0,5 con medio día).
  const locale = useTenantLocale()
  const { data: beneficiosFicha } = useBeneficiosDeLaFicha(employee || null)
  const saldos = beneficiosFicha?.results ?? []
  const [beneficio, setBeneficio] = useState('')
  const [parteDelDia, setParteDelDia] = useState('')
  const [yaAprobado, setYaAprobado] = useState(false)
  const esBeneficio = leaveType === 'benefit'
  const saldo = saldos.find(b => b.beneficio === beneficio)
  const listo = !!employee && !!startDate && (esBeneficio
    ? !!beneficio && (parteDelDia ? true : !!endDate)
    : !!endDate && !!days)

  const crear = () => {
    if (!listo) return
    const payload = esBeneficio
      ? {
          employee, beneficio, start_date: startDate,
          end_date: parteDelDia ? startDate : endDate,
          parte_del_dia: parteDelDia, reason,
          ...(yaAprobado ? { status: 'approved' } : {}),
        }
      : { employee, leave_type: leaveType, start_date: startDate, end_date: endDate, days, reason }
    create(payload, {
      onSuccess: () => { toast.success(esBeneficio ? 'Beneficio registrado' : 'Permiso creado'); onClose() },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  return (
    <div className="p-4 bg-glass-3 border border-divider rounded-xl space-y-3">
      <p className="text-xs font-bold text-content-muted uppercase tracking-wider">Nuevo Permiso / Licencia</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SelectField
          dense
          label="Empleado"
          value={employee}
          onChange={e => setEmployee(e.target.value)}
        >
          <option value="">Seleccionar...</option>
          {(empData?.employees || []).map((e: any) => (
          <option key={e.id} value={e.id}>{e.full_name || `${e.first_name} ${e.last_name}`}</option>
          ))}
        </SelectField>
        <div>
          <label htmlFor="vacationstab-tipo" className={labelCls}>Tipo</label>
          <select id="vacationstab-tipo" value={leaveType} onChange={e => setLeaveType(e.target.value)} className={inputCls}>
            {(saldos.length > 0 || esBeneficio) && <option value="benefit">Beneficio de días</option>}
            <option value="paid_leave">Con Goce de Sueldo</option>
            <option value="unpaid_leave">Sin Goce de Sueldo</option>
            <option value="medical">Licencia Médica (incluye postnatal parental)</option>
            <option value="bereavement">Fallecimiento (Art. 66)</option>
            <option value="paternity">Nacimiento de Hijo (5 días, Art. 195)</option>
            <option value="marriage">Matrimonio o Unión Civil (Art. 207 bis)</option>
            <option value="legal">Otro Permiso Legal</option>
            <option value="other">Otro</option>
          </select>
          <p className="text-[11px] text-content-muted mt-0.5">
            Solo «Sin Goce» y «Licencia Médica» descuentan días del mes: son los que no
            paga la empresa. El postnatal parental va como licencia médica.
          </p>
          {leaveType === 'bereavement' && (
            <div className="mt-2 rounded-md bg-glass-3 border border-divider px-2.5 py-2 text-[11px] text-content-muted">
              <span className="font-semibold text-content">Días del Art. 66</span> (pagados por la
              empresa, adicionales al feriado y no compensables en dinero):
              <ul className="mt-1 space-y-0.5">
                <li>• Hijo — <strong>10 días corridos</strong></li>
                <li>• Hijo en gestación — <strong>7 días hábiles</strong></li>
                <li>• Cónyuge o conviviente civil — <strong>7 días corridos</strong></li>
                <li>• Padre, madre o hermano — <strong>4 días hábiles</strong></li>
              </ul>
            </div>
          )}
        </div>
        {esBeneficio && (
          <div className="col-span-2">
            <SelectField
              dense label="Beneficio" value={beneficio} placeholder="Seleccionar..."
              onChange={e => { setBeneficio(e.target.value); setParteDelDia('') }}
              options={saldos.map(b => ({
                value: b.beneficio,
                label: `${b.nombre} — ${formatoDias(b.disponible, locale)} disponibles`,
              }))}
              hint={saldo?.fechas_validas
                ? (saldo.fechas_validas.length
                    ? `Fechas válidas: ${saldo.fechas_validas.map(f => formatDate(f, locale)).join(', ')}`
                    : 'Sin fecha válida en el período: revisar la fecha de la ficha.')
                : 'Se descuentan los días hábiles del rango.'}
            />
          </div>
        )}
        {esBeneficio && saldo?.permite_medio_dia && (
          <SelectField
            dense label="Jornada" value={parteDelDia}
            onChange={e => setParteDelDia(e.target.value)}
            options={[
              { value: '', label: 'Días completos' },
              { value: 'manana', label: 'Medio día (mañana)' },
              { value: 'tarde', label: 'Medio día (tarde)' },
            ]}
          />
        )}
        <div>
          <label htmlFor="vacationstab-desde" className={labelCls}>{parteDelDia ? 'Fecha' : 'Desde'}</label>
          <DatePicker id="vacationstab-desde" value={startDate} onChange={setStartDate} />
        </div>
        {!parteDelDia && (
          <div>
            <label htmlFor="vacationstab-hasta" className={labelCls}>Hasta</label>
            <DatePicker id="vacationstab-hasta" value={endDate} onChange={setEndDate} />
          </div>
        )}
        {!esBeneficio && (
          <BusinessDaysField startDate={startDate} endDate={endDate} value={days} onChange={setDays} label="Días" />
        )}
        <TextField
          dense
          className="col-span-2"
          label="Motivo"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Motivo del permiso"
          inputProps={{ type: 'text' }}
          required={esBeneficio && !!saldo?.requiere_motivo}
        />
      </div>
      {esBeneficio && (
        <label className="flex items-center gap-2 text-xs text-content">
          <input type="checkbox" checked={yaAprobado} onChange={e => setYaAprobado(e.target.checked)} />
          Registrar como aprobado (días ya tomados sin pasar por el portal)
        </label>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-surface">Cancelar</button>
        <button onClick={crear} disabled={isPending || !listo}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40">
          {esBeneficio ? 'Registrar beneficio' : 'Crear permiso'}
        </button>
      </div>
    </div>
  )
}
